type TelegramOpenLinkOptions = {
  try_instant_view?: boolean;
};

export type OpenExternalLinkOptions = {
  closeTelegramWebApp?: boolean;
  preferCurrentTab?: boolean;
};

export type TelegramLaunchIssue =
  | "none"
  | "sdk_missing"
  | "webapp_missing"
  | "init_data_missing";

export type TelegramLaunchContext = {
  sdkLoaded: boolean;
  webAppAvailable: boolean;
  initDataAvailable: boolean;
  userAgentSuggestsTelegram: boolean;
  isLikelyTelegram: boolean;
  canAuthorizeWithTelegram: boolean;
  issue: TelegramLaunchIssue;
};

const isCustomProtocolUrl = (url: string) => {
  return /^[a-z][a-z0-9+.-]*:/i.test(url) && !/^https?:/i.test(url);
};

const hasTelegramUserAgent = () => {
  if (typeof navigator === "undefined") return false;

  return /Telegram/i.test(navigator.userAgent || "");
};

const openCustomProtocolLink = (url: string) => {
  if (typeof document !== "undefined" && document.body) {
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return true;
  }

  window.location.assign(url);
  return true;
};

export const getTelegramWebApp = () => window.Telegram?.WebApp;

export const getTelegramLaunchContext = (): TelegramLaunchContext => {
  const sdkLoaded = Boolean(window.Telegram);
  const webApp = getTelegramWebApp();
  const webAppAvailable = Boolean(webApp);
  const initDataAvailable = Boolean(webApp?.initData?.trim());
  const userAgentSuggestsTelegram = hasTelegramUserAgent();
  const isLikelyTelegram =
    userAgentSuggestsTelegram || sdkLoaded || webAppAvailable;
  const canAuthorizeWithTelegram = webAppAvailable && initDataAvailable;

  let issue: TelegramLaunchIssue = "none";

  if (isLikelyTelegram) {
    if (!sdkLoaded) {
      issue = "sdk_missing";
    } else if (!webAppAvailable) {
      issue = "webapp_missing";
    } else if (!initDataAvailable) {
      issue = "init_data_missing";
    }
  }

  return {
    sdkLoaded,
    webAppAvailable,
    initDataAvailable,
    userAgentSuggestsTelegram,
    isLikelyTelegram,
    canAuthorizeWithTelegram,
    issue,
  };
};

export const hasTelegramInitData = () =>
  getTelegramLaunchContext().canAuthorizeWithTelegram;

type InitializeTelegramWebAppOptions = {
  headerColor?: string;
};

export const initializeTelegramWebApp = (
  options: InitializeTelegramWebAppOptions = {}
) => {
  const launchContext = getTelegramLaunchContext();

  if (!launchContext.canAuthorizeWithTelegram) {
    return false;
  }

  const tg = getTelegramWebApp();

  if (!tg) {
    return false;
  }

  const { headerColor } = options;

  if (typeof tg.ready === "function") {
    tg.ready();
  }

  if (headerColor && typeof tg.setHeaderColor === "function") {
    const isThemeColor = headerColor === "bg_color" || headerColor === "secondary_bg_color";
    // Custom header colors require Bot API 6.9; older clients only accept theme keys.
    const supportsCustomColor =
      typeof tg.isVersionAtLeast !== "function" || tg.isVersionAtLeast("6.9");

    try {
      tg.setHeaderColor(isThemeColor || supportsCustomColor ? headerColor : "bg_color");
    } catch {
      // A missing version check or a client-specific bridge must not block startup.
      try {
        tg.setHeaderColor("bg_color");
      } catch {
        // Keep the client's default header when its native bridge rejects the update.
      }
    }
  }

  try {
    if (typeof tg.expand === "function") {
      tg.expand();
    }
  } catch {
    if (typeof tg.expand === "function") {
      tg.expand();
    }
  }

  return true;
};

export const openExternalLink = (
  url?: string | null,
  options: OpenExternalLinkOptions = {}
) => {
  if (!url) return false;

  const { closeTelegramWebApp = false, preferCurrentTab = false } = options;
  const customProtocolUrl = isCustomProtocolUrl(url);
  const tg = getTelegramWebApp();
  const tgOpenLink = tg?.openLink as
    | ((url: string, options?: TelegramOpenLinkOptions) => void)
    | undefined;

  if (customProtocolUrl) {
    return openCustomProtocolLink(url);
  }

  if (preferCurrentTab) {
    window.location.assign(url);
    return true;
  }

  if (tgOpenLink) {
    tgOpenLink(url, { try_instant_view: false });

    if (closeTelegramWebApp && typeof tg?.close === "function") {
      tg.close();
    }

    return true;
  }

  const openedWindow = window.open(url, "_blank", "noopener,noreferrer");

  if (!openedWindow) {
    window.location.assign(url);
  }

  return true;
};
